"use client";

import { useEffect } from "react";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { App, type URLOpenListenerEvent } from "@capacitor/app";
import {
  PushNotifications,
  type ActionPerformed,
} from "@capacitor/push-notifications";
import { processAuthDeepLink } from "@/lib/auth/oauth-deep-link";
import { processPaymentDeepLink } from "@/lib/auth/payment-deep-link";
import { processIdentityDeepLink } from "@/lib/auth/identity-deep-link";
import { isDuplicateDeepLink } from "@/lib/auth/deep-link-dedup";
import { logNativeFlow } from "@/lib/native-flow-diagnostics";
import { scheduleCrewInstallReferrer } from "@/lib/crew/install-referrer";

/**
 * 일반 링크(https universal/app link, Smart App Banner app-argument)로 라우팅한 URL 기록.
 * Capacitor 는 launch URL 을 프로세스 수명 내내 들고 있어(Android intent data, iOS lastURL)
 * 페이지가 full reload 될 때마다 getLaunchUrl 이 같은 값을 다시 돌려준다. dedup(10분 TTL)이
 * 만료된 뒤에도 같은 링크로 되끌려 가지 않도록, WebView 세션 동안 한 번만 라우팅한다.
 */
const ROUTED_LINKS_KEY = "la_routed_deep_links";

function wasRouted(url: string): boolean {
  try {
    const list = JSON.parse(sessionStorage.getItem(ROUTED_LINKS_KEY) || "[]") as string[];
    return Array.isArray(list) && list.includes(url);
  } catch {
    return false;
  }
}

function markRouted(url: string): void {
  try {
    const list = JSON.parse(sessionStorage.getItem(ROUTED_LINKS_KEY) || "[]") as string[];
    const next = [url, ...(Array.isArray(list) ? list.filter((u) => u !== url) : [])].slice(0, 20);
    sessionStorage.setItem(ROUTED_LINKS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

/** cold start 로 라우팅해도 되는 호스트 — 우리 도메인만(앱이 연 임의 URL 로 가지 않는다). */
function isOwnHttpsLink(url: URL): boolean {
  if (url.protocol !== "https:") return false;
  const host = url.host.toLowerCase();
  return (
    host === "locarora.com" ||
    host === "www.locarora.com" ||
    host === window.location.host.toLowerCase()
  );
}

/**
 * Capacitor native 앱의 deep link / URL open 이벤트 처리.
 *
 * iOS 의 OAuth flow 는 ASWebAuthenticationSession 이 callback URL 을 동기적으로
 * 돌려주므로 이 리스너가 없어도 동작한다. 그러나 Android 의 OAuth flow 는
 * Browser.open + OS 의 intent dispatch 로 동작하기 때문에 JS 측에 appUrlOpen
 * 리스너가 등록되어 있지 않으면 deep link URL 이 그대로 소실되어 setSession
 * 호출 자체가 일어나지 않는다.
 *
 * Cold start (앱이 종료된 상태에서 deep link 로 launch) 도 함께 처리한다 —
 * `App.getLaunchUrl()` 한 번만 호출하면 OS 가 보관해 둔 launch URL 을 회수
 * 가능.
 *
 * 호출 위치: root layout 안 client 영역에 한 번만 마운트. 모든 페이지에서 살아있어야 함.
 */
export function CapacitorBootstrap() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // iOS universal link interception: SFSafariViewController 안에서
    // PortOne 이 우리 bridge callback URL(`https://locarora.com/.../payment-
    // bridge-callback?...`) 로 redirect 하면 iOS 가 universal link 매칭으로
    // 그걸 catch 해서 SFSafariViewController 를 닫고 우리 앱한테 직접
    // appUrlOpen 으로 전달한다. bridge 페이지의 setTimeout → locarora://
    // 발화 단계가 short-circuit 됨. HTTPS bridge URL 그대로는 우리
    // handler 들이 (locarora: protocol 만 매칭) 처리 못 해서 fallthrough 가
    // query 없이 path 만 redirect → 빈 paymentId 로 실패. 여기서 미리
    // locarora:// 스킴 으로 normalize 해 handler 들이 정상 처리하게 한다.
    const normalizeUniversalLink = (urlStr: string): string => {
      try {
        const url = new URL(urlStr);
        if (url.protocol !== "https:") return urlStr;
        const path = url.pathname;
        if (path.endsWith("/checkout/payment-bridge-callback")) {
          return `locarora://payment-complete${url.search}`;
        }
        if (path.endsWith("/identity-bridge-callback")) {
          return `locarora://identity-complete${url.search}`;
        }
        return urlStr;
      } catch {
        return urlStr;
      }
    };

    const dispatch = async (urlStr: string): Promise<boolean> => {
      const normalizedUrl = normalizeUniversalLink(urlStr);
      if (normalizedUrl !== urlStr) {
        logNativeFlow("deeplink", "universal-link.normalized", {
          original: urlStr,
          normalized: normalizedUrl,
        });
      }

      // 중앙집중식 dedup: 같은 URL 이 짧은 시간 안에 두 번 이상 들어오면
      // 무시. iOS 의 appUrlOpen 중복 발화 / getLaunchUrl + appUrlOpen race /
      // window.location.href reload 후 getLaunchUrl 재실행 / Safari smart
      // banner 재발화 등 모든 케이스를 한 곳에서 막는다. true 를 돌려주는
      // 건 "처리됐다" 는 의미라서 호출자도 일반 라우팅으로 fallthrough 안 함.
      if (isDuplicateDeepLink(normalizedUrl)) {
        logNativeFlow("deeplink", "duplicate-suppressed", {
          url: normalizedUrl,
        });
        return true;
      }
      if (await processAuthDeepLink(normalizedUrl)) return true;
      if (await processPaymentDeepLink(normalizedUrl)) return true;
      if (await processIdentityDeepLink(normalizedUrl)) return true;
      return false;
    };

    const handleDeepLink = async (event: URLOpenListenerEvent) => {
      if (await dispatch(event.url)) return;

      // 일반 deep link (locarora://product/123 등): pathname + query + hash
      // 까지 보존해서 라우팅. query 를 빼먹으면 결제 / 인증 callback URL
      // 처럼 query 가 의미를 갖는 경로에서 정보 손실로 fallback 처리 실패.
      //
      // `/r/{code}`(크루 링크)도 여기서 **full navigation** 으로 간다 — 라우트 핸들러가 앱
      // WebView 안에서 클릭을 적재하고 la_ref 를 앱 쿠키 저장소에 심은 뒤 상품으로 302 한다.
      try {
        const url = new URL(event.url);
        if (url.pathname) {
          markRouted(event.url);
          window.location.href = url.pathname + url.search + url.hash;
        }
      } catch {
        // malformed URL — 무시
      }
    };

    void App.addListener("appUrlOpen", handleDeepLink);

    // 푸시 알림 탭 → 알림 data.url 로 이동. root layout 에 상주하는 이 컴포넌트에서
    // 전역 등록해, 어느 화면에서 탭해도 딥링크되게 한다 (usePushNotifications 훅은
    // 특정 페이지에서만 마운트되므로 거기 두면 대부분의 탭이 유실됨).
    let pushActionHandle: PluginListenerHandle | undefined;
    void PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (action: ActionPerformed) => {
        const url = action.notification?.data?.url;
        if (url && typeof window !== "undefined") {
          window.location.href = url;
        }
      }
    ).then((handle) => {
      pushActionHandle = handle;
    });

    let cancelInstallReferrer: (() => void) | undefined;
    let unmounted = false;

    void App.getLaunchUrl()
      .then(async (result): Promise<boolean> => {
        if (!result?.url) return false;
        if (await dispatch(result.url)) return true;
        // 앱이 꺼진 상태에서 https 링크(universal/app link — 크루 /r/{code},
        // Smart App Banner app-argument)로 열린 경우. appUrlOpen 은 cold start 에
        // 오지 않으므로(Android) 여기서 같은 규칙으로 라우팅한다.
        try {
          const url = new URL(result.url);
          if (!isOwnHttpsLink(url) || wasRouted(result.url)) return false;
          markRouted(result.url);
          const target = url.pathname + url.search + url.hash;
          if (target !== window.location.pathname + window.location.search + window.location.hash) {
            window.location.href = target;
            return true;
          }
        } catch {
          // malformed URL — 무시
        }
        return false;
      })
      .catch(() => {
        // 무시 — cold start 가 아니면 result 가 null 인 게 정상
        return false;
      })
      .then((routedByLink) => {
        // Android 첫 실행: Play Install Referrer 로 넘어온 크루 클릭을 채택한다.
        // 링크로 이미 다른 화면에 가는 중이면 그 이동이 가라앉은 뒤로 미루고(이동 중에 띄운
        // 서버 액션은 중단된다), 이동은 하지 않고 채택만 한다. 실패하면 다음 실행에 다시.
        if (unmounted) return;
        cancelInstallReferrer = scheduleCrewInstallReferrer(routedByLink);
      });

    return () => {
      unmounted = true;
      cancelInstallReferrer?.();
      void App.removeAllListeners();
      void pushActionHandle?.remove();
    };
  }, []);

  return null;
}
